import { LoadingStateType, LoadingStatuses } from '@/common/constants';
import AppChip from '@/ui/chip/AppChip';
import classNames from 'classnames';
import { Button } from 'primereact/button';
import { Tooltip } from 'primereact/tooltip';
import { TooltipOptions } from 'primereact/tooltip/tooltipoptions';
import React, { HTMLAttributes, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

const BetaWrapper = styled.div`
  position: relative;
`;

const BetaChipWrapper = styled.div`
  position: absolute;
  top: -14px;
  left: 24px;
`;

const BetaChip = styled(AppChip)`
  &.p-chip {
    color: var(--red-darkest);
    background: var(--red-light);
  }
`;

const StyledButton = styled(Button)`
  direction: ltr;
  &.p-button {
    flex-shrink: 0;
    font-size: var(--small-font-size);
    line-height: var(--small-line-height);
    background: var(--red-dark);
    color: var(--white-main);
    border: 1px solid var(--red-dark);
    border-radius: var(--default-border-radius);
    padding: 10px var(--default-padding);
    height: var(--default-input-height);
    overflow: hidden;

    &.p-button-text {
      border-color: transparent !important;
      background: transparent !important;
      color: var(--red-dark) !important;
    }

    &.p-button-icon-only {
      font-size: var(--medium-font-size);
      line-height: var(--small-line-height);
      border-radius: var(--default-border-radius);
      padding: 10px var(--default-padding);
      height: var(--default-input-height);

      &.p-button-md {
        height: var(--medium-button-height);
        width: var(--medium-button-height);
        padding: 10px var(--small-padding);
      }
    }

    &.p-button-success,
    &.p-button-warning,
    &.p-button-help,
    &.p-button-plain,
    &.p-button-danger {
      background: var(--red-dark);
      color: var(--white-main);
      border: 1px solid var(--red-dark);
    }

    &.p-button-basic {
      background: var(--white-main);
      color: var(--purple-middle);
      border: 1px solid var(--white-main);
    }

    &.p-button-info {
      background-color: var(--gray-opacity);
      color: var(--black-main);
      border: 1px solid transparent;

      &.p-button-text {
        border-color: transparent !important;
        background: transparent !important;
        color: var(--gray-opacity) !important;
      }
    }

    &.p-button-dropdown {
      background-color: var(--beige-main);
      color: var(--black-main);
      border: 1px solid transparent;

      .p-button-label {
        text-align: left;
        font-weight: 400;
      }

      .p-button-icon {
        color: var(--gray-darker);
      }
    }

    &.p-button-secondary {
      background: var(--purple-middle);
      color: var(--white-main);
      border: 1px solid var(--purple-middle);

      &.p-button-text {
        border-color: transparent !important;
        background: transparent !important;
        color: var(--purple-middle) !important;
        &.group-button {
          height: auto;
          border-color: var(--beige-main) !important;
          background: var(--beige-main) !important;
          border-top-left-radius: 0px;
          border-bottom-left-radius: 0px;
          padding-left: var(--xsmall-padding);
          &:enabled:hover {
            background: var(--beige-main) !important;
            border-color: var(--beige-main) !important;
          }
        }

        &.poster-actions.p-button-icon-only {
          padding: var(--xxsmall-padding);
          width: 24px;
          height: 24px;
        }
      }
      &.beige-background {
        border-color: transparent !important;
        background: var(--beige-main) !important;
        color: var(--purple-middle) !important;
        &:enabled:hover {
          border-color: transparent !important;
          background: var(--red-darkest) !important;
          color: var(--white-main) !important;
        }
      }
      &.white-background {
        border-color: transparent !important;
        background: var(--white-main) !important;
        color: var(--purple-middle) !important;
        &:enabled:hover {
          border-color: transparent !important;
          background: var(--white-main) !important;
          color: var(--red-dark) !important;
        }
      }
    }

    &.p-button-outlined {
      background: var(--white-main) !important;
      color: var(--purple-middle) !important;
      border: 1px solid var(--purple-middle) !important;
    }

    &.p-button-rounded {
      border-radius: 50%;
      padding: var(--small-padding);
      height: var(--default-input-height);
      width: var(--default-input-height);
    }

    &:hover,
    &:enabled:hover,
    &.p-button-secondary:enabled:hover,
    &.p-button-outlined:enabled:hover &.p-button-success:enabled:hover,
    &.p-button-warning:enabled:hover,
    &.p-button-help:enabled:hover,
    &.p-button-plain:enabled:hover,
    &.p-button-danger:enabled:hover {
      background: var(--red-darkest) !important;
      color: var(--white-main) !important;
      border: 1px solid var(--red-darkest) !important;
    }

    &.p-button-basic:enabled:hover {
      background: var(--white-main) !important;
      color: var(--purple-middle) !important;
      border: 1px solid var(--white-main) !important;
    }

    &.p-button-info:enabled:hover {
      background: var(--red-pale) !important;
      color: var(--red-middle) !important;
      border: 1px solid var(--red-pale) !important;

      &.p-button-outlined {
        background: var(--purple-middle) !important;
        color: var(--white-main) !important;
        border: 1px solid var(--purple-middle) !important;
      }
    }

    &.p-button-dropdown:enabled:hover {
      background: var(--beige-main) !important;
      color: var(--black-main) !important;
      border: 1px solid var(--red-main) !important;
      box-shadow: none !important;
    }

    &.p-button-dropdown:enabled:focus {
      box-shadow: none !important;
    }

    &.p-button-text:enabled:hover {
      color: var(--red-dark) !important;
      background: transparent !important;
      border-color: transparent !important;
    }

    .p-button-label {
      font-weight: 500;
      transition: none;
    }

    &.p-button-info:enabled:active {
      box-shadow: var(--box-shadow-red) !important;
      background: var(--red-dark) !important;
      color: var(--white-main) !important;
      border: 1px solid var(--red-dark) !important;
    }

    &:focus,
    &:enabled:focus,
    &.p-button-secondary:enabled:focus {
      box-shadow: var(--box-shadow-red) !important;
    }

    &.p-button-xs {
      padding: 9px;
      height: 32px;
      width: 32px;
    }

    &.p-button-sm {
      height: var(--medium-button-height);
    }

    &.p-button-simple {
      background: transparent;
      color: var(--black-main);
      border: 1px solid var(--gray-darker);
      font-weight: 500;
      &.p-button-outlined {
        border-color: var(--gray-darker) !important;
        background: transparent;
        color: var(--black-main) !important;
        &:enabled:hover {
          background: transparent !important;
          color: var(--black-main) !important;
          border: 1px solid var(--gray-darker) !important;
        }
      }

      .visible {
        color: var(--gray-darker);
      }
    }

    &.p-button-underline,
    &.p-button-underline-always {
      height: auto;
      background: transparent;
      color: var(--purple-dark);
      border: none;
      border-bottom: 2px solid var(--gray-border-light);
      border-radius: 0;
      padding: 0;
      padding-bottom: 5px;
      font-weight: 500;

      &:enabled:hover,
      &:enabled:focus,
      &:hover,
      &.p-button-underline-always {
        background: transparent !important;
        color: var(--purple-dark) !important;
        border: none !important;
        border-bottom: 2px solid var(--red-dark) !important;
        box-shadow: none !important;
      }
    }

    &.p-button-underline-always {
      &.p-button-xs {
        color: var(--black-main) !important;
        font-size: var(--xsmall-font-size) !important;
        line-height: 150% !important;
        padding-bottom: 0;
      }
    }

    &.p-button-md {
      height: 42px;
      font-size: var(--small-font-size);
      line-height: var(--small-line-height);
      padding: 10px var(--default-padding);
    }

    &.p-button-no-background {
      background: transparent !important;
    }
  }
`;

export type AppButtonProps = {
  label?: string;
  type?: 'text' | 'outlined';
  isSubmit?: boolean;
  severity?:
    | 'secondary'
    | 'success'
    | 'info'
    | 'warning'
    | 'help'
    | 'danger'
    | 'plain'
    | 'basic'
    | 'simple'
    | 'no-background'
    | 'underline'
    | 'underline-always'
    | 'dropdown';
  icon?: string | ReactNode;
  iconPos?: 'right' | 'left';
  onClick?: any;
  state?: LoadingStateType;
  isDisabled?: boolean;
  size?: 'xs' | 'sm' | 'md' | 'lg';
  rounded?: boolean;
  tooltip?: string;
  tooltipOptions?: TooltipOptions;
  ariaLabel?: string;
  dataId?: string;
  children?: ReactNode;
  autoFocus?: boolean;
  rtl?: boolean;
  isBeta?: boolean;
  showBetaTooltip?: boolean;
} & HTMLAttributes<HTMLButtonElement>;

const AppButton: React.FC<AppButtonProps> = ({
  label,
  type,
  isSubmit,
  severity,
  icon,
  iconPos,
  className,
  onClick,
  state = LoadingStatuses.IDLE,
  isDisabled = false,
  size,
  rounded = false,
  tooltip,
  tooltipOptions,
  ariaLabel,
  id,
  dataId,
  children,
  autoFocus = false,
  rtl = false,
  isBeta = false,
  showBetaTooltip = true,
}) => {
  const { t } = useTranslation();
  const styles = classNames(
    'outline-none',
    {
      [`p-button-${size}`]: size,
      'p-button-danger': state === 'error',
      [`p-button-${severity}`]: severity,
      [`p-button-${type}`]: type,
    },
    className,
  );

  const stateIcon = classNames({
    'pi pi-spin pi-spinner': state === LoadingStatuses.LOADING,
    'pi pi-times': state === 'error',
  });

  const iconPosition = () => {
    if (!iconPos) {
      return rtl ? 'right' : 'left';
    }
    return rtl ? (iconPos === 'left' ? 'right' : 'left') : iconPos;
  };

  if (rtl) {
    tooltipOptions = {
      ...tooltipOptions,
      style: { ...tooltipOptions?.style, direction: 'rtl' },
    };
  }

  const button = (
    <StyledButton
      id={id}
      label={children ? undefined : label}
      type={isSubmit ? 'submit' : 'button'}
      icon={state !== LoadingStatuses.IDLE ? stateIcon : icon}
      iconPos={iconPosition()}
      className={styles}
      disabled={
        isDisabled ||
        [LoadingStatuses.LOADING, LoadingStatuses.ERROR].includes(state)
      }
      aria-label={ariaLabel}
      onClick={onClick}
      rounded={rounded}
      tooltip={isBeta && showBetaTooltip ? undefined : tooltip}
      tooltipOptions={
        isBeta && showBetaTooltip
          ? undefined
          : { showOnDisabled: true, ...tooltipOptions }
      }
      data-id={dataId}
      autoFocus={autoFocus}
    >
      {children}
    </StyledButton>
  );

  if (isBeta) {
    const betaTooltipId = `beta-btn-${id ?? 'default'}`;

    return (
      <BetaWrapper className={betaTooltipId}>
        {showBetaTooltip && (
          <Tooltip
            target={`.${betaTooltipId}`}
            position={tooltipOptions?.position ?? 'bottom'}
            style={tooltipOptions?.style}
          >
            <b>{t('generic.beta.tooltip')} </b>
            {tooltip}
          </Tooltip>
        )}
        {button}
        <BetaChipWrapper>
          <BetaChip label={t('generic.beta')} type="red" size="s" />
        </BetaChipWrapper>
      </BetaWrapper>
    );
  }

  return button;
};

export default AppButton;
